import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ElapsedTimer } from '@/components/ElapsedTimer';
import { RecordingStatus } from '@/components/RecordingStatus';
import { SyncStatus } from '@/components/SyncStatus';
import { VehicleCounterCard } from '@/components/VehicleCounterCard';
import { ACTIVE_VEHICLE_TYPES, createEmptyCounts, vehicleTypeName } from '@/config/vehicleTypes';
import {
  getSurvey,
  hasUndoableCount,
  loadCounts,
  recordCount,
  saveCountsSnapshot,
  undoLastCount,
  updateSurvey,
} from '@/db/localDatabase';
import { useConfirm } from '@/hooks/useConfirm';
import { useSurveyTimer } from '@/hooks/useSurveyTimer';
import type { Survey, VehicleCounts } from '@/types/survey';
import { totalCount } from '@/utils/counts';
import { formatDuration } from '@/utils/duration';
import { formatNumber } from '@/utils/format';

/**
 * How often the denormalised snapshot on the survey record is refreshed while
 * counting. The authoritative data is the per-tap event row, which is written
 * immediately; this only keeps the summary/history view cheap to read.
 */
const SNAPSHOT_INTERVAL_MS = 3_000;

export function CountingPage() {
  const { surveyId = '' } = useParams();
  const navigate = useNavigate();
  const { pending, confirm, answer } = useConfirm();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [counts, setCounts] = useState<VehicleCounts>(createEmptyCounts);
  const [canUndo, setCanUndo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<string | null>(null);

  const countsRef = useRef<VehicleCounts>(counts);
  const snapshotTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapshotDirty = useRef(false);
  const startingRef = useRef(false);

  countsRef.current = counts;

  // --- load / recover -------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const record = await getSurvey(surveyId);
      if (cancelled) return;
      if (!record) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      // Counts are always rebuilt from the append-only event log, so a refresh,
      // a crash or a PWA restart cannot lose a tap.
      const [restored, undoable] = await Promise.all([
        loadCounts(surveyId),
        hasUndoableCount(surveyId),
      ]);
      if (cancelled) return;
      setSurvey(record);
      setCounts(restored);
      setCanUndo(undoable);
      setLoading(false);

      if (
        record.status === 'completed' ||
        record.status === 'pending_sync' ||
        record.status === 'synced'
      ) {
        navigate(`/summary/${surveyId}`, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [surveyId, navigate]);

  // --- snapshot persistence -------------------------------------------------
  const flushSnapshot = useCallback(async () => {
    if (!snapshotDirty.current) return;
    snapshotDirty.current = false;
    try {
      await saveCountsSnapshot(surveyId, countsRef.current);
    } catch (error) {
      console.error('Snapshot write failed', error);
    }
  }, [surveyId]);

  const scheduleSnapshot = useCallback(() => {
    snapshotDirty.current = true;
    if (snapshotTimer.current) return;
    snapshotTimer.current = setTimeout(() => {
      snapshotTimer.current = null;
      void flushSnapshot();
    }, SNAPSHOT_INTERVAL_MS);
  }, [flushSnapshot]);

  // Flush when the app is backgrounded or closed — the moment data is most at risk.
  useEffect(() => {
    const flush = () => {
      void flushSnapshot();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
      if (snapshotTimer.current) clearTimeout(snapshotTimer.current);
      flush();
    };
  }, [flushSnapshot]);

  // --- actions --------------------------------------------------------------
  const handleIncrement = useCallback(
    (vehicleTypeId: string) => {
      // 1. UI first — the surveyor must never wait for storage.
      setCounts((current) => ({
        ...current,
        [vehicleTypeId]: (current[vehicleTypeId] ?? 0) + 1,
      }));
      setCanUndo(true);
      setLastAction(`+1 ${vehicleTypeName(vehicleTypeId)}`);

      // 2. Then the durable write (one tiny row), without blocking the tap.
      void recordCount(surveyId, vehicleTypeId).catch((error) => {
        console.error('Count write failed', error);
        setWriteError(
          'This phone refused to save the last count. Stop and check free storage before continuing.',
        );
      });
      scheduleSnapshot();
    },
    [surveyId, scheduleSnapshot],
  );

  const handleUndo = useCallback(async () => {
    const vehicleTypeId = await undoLastCount(surveyId);
    if (!vehicleTypeId) {
      setCanUndo(false);
      setLastAction('Nothing left to undo');
      return;
    }
    setCounts((current) => ({
      ...current,
      [vehicleTypeId]: Math.max(0, (current[vehicleTypeId] ?? 0) - 1),
    }));
    setLastAction(`Undid 1 ${vehicleTypeName(vehicleTypeId)}`);
    setCanUndo(await hasUndoableCount(surveyId));
    scheduleSnapshot();
  }, [surveyId, scheduleSnapshot]);

  const handleStart = useCallback(async () => {
    if (startingRef.current || !survey) return;
    startingRef.current = true;
    try {
      const actualStartTimestamp = Date.now();
      await updateSurvey(survey.id, { status: 'recording', actualStartTimestamp });
      setSurvey({ ...survey, status: 'recording', actualStartTimestamp });
    } finally {
      startingRef.current = false;
    }
  }, [survey]);

  const elapsed = useSurveyTimer(survey?.actualStartTimestamp, survey?.actualEndTimestamp);

  const handleEnd = useCallback(async () => {
    if (!survey) return;
    const ok = await confirm({
      title: 'End vehicle counting?',
      body: `Elapsed Time: ${formatDuration(elapsed)}\nTotal so far: ${formatNumber(
        totalCount(countsRef.current),
      )} vehicles\n\nCounting cannot be restarted afterwards.`,
      confirmLabel: 'End Survey',
      cancelLabel: 'Continue Counting',
      destructive: true,
    });
    if (!ok) return;

    await flushSnapshot();
    const actualEndTimestamp = Date.now();
    await saveCountsSnapshot(survey.id, countsRef.current);
    await updateSurvey(survey.id, { status: 'completed', actualEndTimestamp });
    navigate(`/summary/${survey.id}`, { replace: true });
  }, [survey, confirm, elapsed, flushSnapshot, navigate]);

  const total = useMemo(() => totalCount(counts), [counts]);

  // --- render ---------------------------------------------------------------
  if (loading) {
    return (
      <div className="page-body">
        <p className="text-slate-500">Restoring survey…</p>
      </div>
    );
  }

  if (notFound || !survey) {
    return (
      <div className="page-body space-y-4">
        <h1 className="text-lg font-bold">Survey not found</h1>
        <p className="text-slate-600">
          This survey is not stored on this device. It may have been recorded on another phone.
        </p>
        <button type="button" className="btn-primary w-full" onClick={() => navigate('/')}>
          Start a new survey
        </button>
      </div>
    );
  }

  const notStarted = survey.status !== 'recording';

  return (
    /*
     * A fixed, non-scrolling viewport. The whole point of this screen is that a
     * surveyor watching the road can reach any of the 15 categories without
     * looking down to scroll, so the grid is sized to the screen rather than the
     * screen being sized to the grid.
     */
    <div className="fixed inset-0 mx-auto flex max-w-3xl flex-col overflow-hidden bg-white">
      {/* Deliberately minimal chrome: no back button next to the counting grid. */}
      <header className="shrink-0 border-b border-slate-200 bg-white pt-safe">
        <div className="flex items-center justify-between gap-2 px-3 py-1.5">
          <RecordingStatus status={survey.status} />
          <ElapsedTimer
            startTimestamp={survey.actualStartTimestamp}
            endTimestamp={survey.actualEndTimestamp}
            compact
          />
          <SyncStatus />
        </div>
        <div className="flex items-baseline justify-between gap-3 px-3 pb-1.5 text-xs">
          <span className="truncate text-slate-500">
            {survey.roadNumber} · {survey.direction}
          </span>
          <span className="shrink-0 font-bold text-slate-900">Total {formatNumber(total)}</span>
        </div>
      </header>

      {writeError ? (
        <div
          className="shrink-0 border-b-2 border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900"
          role="alert"
        >
          {writeError}
        </div>
      ) : null}

      {/* min-h-0 lets this flex child actually shrink, which is what makes the
          grid resolve to the leftover height instead of overflowing. */}
      <main className="min-h-0 flex-1 p-1.5">
        <div className="vehicle-grid h-full">
          {ACTIVE_VEHICLE_TYPES.map((vehicleType) => (
            <VehicleCounterCard
              key={vehicleType.id}
              vehicleType={vehicleType}
              count={counts[vehicleType.id] ?? 0}
              disabled={notStarted}
              onIncrement={handleIncrement}
            />
          ))}
        </div>
      </main>

      {/* Announced to screen readers without stealing focus. */}
      <p className="sr-only" role="status" aria-live="polite">
        {lastAction}
      </p>

      <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-2 pb-safe">
        {notStarted ? (
          <>
            <p className="mb-2 text-center text-xs text-slate-500">
              Vehicle buttons stay locked until you start, so a stray tap cannot add a vehicle.
            </p>
            <button type="button" className="btn-success w-full text-lg" onClick={handleStart}>
              Start Count
            </button>
          </>
        ) : (
          <div className="flex gap-2">
            {/* Undo is the frequently used control, so it gets the width.
                End Survey is smaller and confirms before doing anything. */}
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={handleUndo}
              disabled={!canUndo}
            >
              Undo Last Count
            </button>
            <button type="button" className="btn-danger shrink-0 px-4" onClick={handleEnd}>
              End
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog request={pending} onAnswer={answer} />
    </div>
  );
}
